import { createActions, createReducer } from 'reduxsauce'
import Immutable, { type ImmutableObject } from 'seamless-immutable'

import type { AppSessionState } from 'APP/Store/AppSession/types'
import type { AppStateStatus } from 'react-native'
import { logger } from 'APP/Lib/Datadog'

/* ------------- Types and Action Creators ------------- */

const actionDefs = {
  setAppForegrounded: null,
  setAppBackgrounded: null,
  syncAppState: ['appState'],
  setSystemError: ['errorMessage', 'fatal'],
  clearSystemError: ['index'],
  setDarkModeRestartRequired: ['darkModeRestartRequired'],
  setIsConnected: ['isConnected'],
  setAppBackgroundedTimestamp: ['backgroundTimestamp'],
} as const satisfies Record<string, string[] | null>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const AppSessionTypes = Types
export default Creators

type ImmutableAppSessionState = ImmutableObject<AppSessionState>

export const INITIAL_STATE: ImmutableAppSessionState = Immutable<AppSessionState>({
  isActive: true,
  isConnected: true,
  systemErrors: [],
  systemExperiencedFatalError: null,
  darkModeRestartRequired: null,
  backgroundTimestamp: null,
})

export const setSystemError = (
  state: ImmutableAppSessionState,
  { errorMessage, fatal }: { errorMessage: string; fatal: boolean }
) => {
  let errors = [...state.systemErrors]
  errors.push({
    errorMessage: String(errorMessage),
    timestamp: Date.now(),
    fatal,
  })
  return state.merge({
    systemErrors: errors,
    systemExperiencedFatalError: state.systemExperiencedFatalError || fatal,
  })
}

export const clearSystemError = (
  state: ImmutableAppSessionState,
  { index }: { index: AppSessionState['systemErrors'][number] }
) =>
  state.merge({
    systemErrors: state.systemErrors ? state.systemErrors.filter((i) => i !== index) : [],
  })

export const setDarkModeRestartRequired = (
  state: ImmutableAppSessionState,
  { darkModeRestartRequired }: { darkModeRestartRequired: boolean }
) => state.merge({ darkModeRestartRequired: !!darkModeRestartRequired })

export const setAppForegrounded = (state: ImmutableAppSessionState) => {
  logger.debug('setAppForegrounded')
  return state.merge({ isActive: true })
}

export const setAppBackgrounded = (state: ImmutableAppSessionState) => {
  logger.debug('setAppBackgrounded')
  return state.merge({ isActive: false })
}

export const syncAppState = (
  state: ImmutableAppSessionState,
  { appState }: { appState: AppStateStatus }
) => {
  const currentReduxIsActive = state.isActive
  const isActive = appState === 'active'

  if (currentReduxIsActive !== isActive) {
    logger.info('Redux isActive value does not match current state, syncing...', {
      isActive,
      currentAppState: appState,
      currentReduxIsActive,
    })
  }

  return state.merge({ isActive })
}

export const setIsConnected = (
  state: ImmutableAppSessionState,
  { isConnected }: { isConnected: boolean | null }
) => state.merge({ isConnected })

export const setAppBackgroundedTimestamp = (
  state: ImmutableAppSessionState,
  { backgroundTimestamp }: { backgroundTimestamp: number | null }
) => state.merge({ backgroundTimestamp })

export const reducer = createReducer<TPayloads, TActions, ImmutableAppSessionState>(INITIAL_STATE, {
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_SYSTEM_ERROR]: setSystemError,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CLEAR_SYSTEM_ERROR]: clearSystemError,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_DARK_MODE_RESTART_REQUIRED]: setDarkModeRestartRequired,
  [Types.SET_APP_FOREGROUNDED]: setAppForegrounded,
  [Types.SET_APP_BACKGROUNDED]: setAppBackgrounded,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SYNC_APP_STATE]: syncAppState,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_IS_CONNECTED]: setIsConnected,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_APP_BACKGROUNDED_TIMESTAMP]: setAppBackgroundedTimestamp,
})
